import { TFunction } from 'i18next';

export type ValidationRule = (value: any) => string;

// ============================================================================
// Basic Validation Rules
// ============================================================================

/**
 * Validates if a value is not empty
 */
export const required = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (value === null || value === undefined) return t('validation.required');
        if (typeof value === 'string' && value.trim() === '') return t('validation.required');
        if (Array.isArray(value) && value.length === 0) return t('validation.required');
        return '';
    };
};

/**
 * Validates if an array is not empty
 */
export const requiredArray = (t: TFunction): ValidationRule => {
    return (value: any) => {
        const isValid = Array.isArray(value) ? value.length > 0 : !!value;
        return isValid ? '' : t('validation.requiredArray');
    };
};

/**
 * Validates if a value is not negative
 */
export const notNegative = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (value === null || value === undefined || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return t('validation.notNegative');
        return num >= 0 ? '' : t('validation.notNegative');
    };
};

/**
 * Validates that value does not contain whitespaces
 */
export const noWhitespace = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return ''; // Empty values handled by required
        return value.includes(' ') ? t('validation.noWhitespaces') : '';
    };
};

/**
 * Validates if a name contains only allowed characters
 * Allowed: a-z, A-Z, 0-9, -, =, _
 */
export const limitSpecialCharacters = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return ''; // Empty values handled by required
        return /^[a-zA-Z0-9_\-=]+$/.test(value) ? '' : t('validation.limitSpecialCharacters');
    };
};

/**
 * Validates that value is a valid URL
 */
export const validUrl = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return ''; // Empty values handled by required

        const pattern = new RegExp(
            '^(https?:\\/\\/)?' +
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
                '((\\d{1,3}\\.){3}\\d{1,3}))' +
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
                '(\\?[;&a-z\\d%_.~+=-]*)?' +
                '(\\#[-a-z\\d_]*)?$',
            'i'
        );

        return pattern.test(value) ? '' : t('validation.invalidUrl');
    };
};

/**
 * Validates if an email address is valid
 */
export const validEmail = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return ''; // Empty values handled by required

        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return pattern.test(value) ? '' : t('validation.invalidEmail');
    };
};

/**
 * Validates if an IP address is valid
 */
export const ipAddress = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return t('validation.required');

        const nibbles = String(value).split('.');

        const nibbleValid = (nibble: string) => {
            if (nibble.length > 3 || nibble.length === 0) return false;
            if (nibble[0] === '0' && nibble !== '0') return false;
            if (!nibble.match(/^\d+$/)) return false;
            const numeric = +nibble || 0;
            return numeric >= 0 && numeric <= 255;
        };

        return nibbles.length === 4 && nibbles.every(nibbleValid) ? '' : t('validation.invalidIp');
    };
};

/**
 * Validates if a port number is within the valid range (1-65534)
 */
export const port = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value && value !== 0) return t('required');

        const num = Number(value);
        if (isNaN(num)) return t('validation.portGreaterThanZero');
        if (num <= 0) return t('validation.portGreaterThanZero');
        if (num >= 65535) return t('validation.portLessThanMax');

        return '';
    };
};

/**
 * Validates password requirements (8-20 characters, only ASCII printable characters)
 */
export const passwordValidation = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return '';

        const strValue = String(value);
        if (strValue.length < 8) return t('validation.passwordMinLength');
        if (strValue.length > 20) return t('validation.passwordMaxLength');

        // Only allow ASCII printable characters (space through tilde: ASCII 32-126)
        const validPasswordPattern = /^[\x20-\x7E]+$/;
        if (!validPasswordPattern.test(strValue)) {
            return t('validation.passwordInvalidChars') || 'Password can only contain standard keyboard characters';
        }

        return '';
    };
};

/**
 * Validates a user's name (first name or last name)
 * Allows letters, numbers, spaces, hyphens, and apostrophes
 * Examples: "John", "Mary-Jane", "O'Connor", "Admin 1"
 */
export const validateUserName = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value || !value.trim()) {
            return t('validation.required');
        }

        const trimmed = String(value).trim();

        // Allow letters (including accented), numbers, spaces, hyphens, and apostrophes
        const validNamePattern = /^[a-zA-Z0-9À-ÿ\s'\-]+$/;
        if (!validNamePattern.test(trimmed)) {
            return (
                t('validation.userNameInvalidChars') ||
                'Name can only contain letters, numbers, spaces, hyphens, and apostrophes'
            );
        }

        return '';
    };
};

/**
 * Validates if a device CID is valid (32-character hexadecimal string).
 * Note: Empty values return valid. Use in combination with required() if it becomes mandatory.
 */
export const validateCid = (t: TFunction): ValidationRule => {
    return (value: any) => {
        // Return valid if empty, since CID is optional on create
        if (!value || String(value).trim() === '') return '';

        // Strictly match exactly 32 hexadecimal characters (0-9, a-f, A-F)
        const pattern = /^[a-fA-F0-9]{32}$/;
        return pattern.test(String(value)) ? '' : t('validation.invalidCid');
    };
};

// ============================================================================
// Composite Validation Rules (Multiple checks in one)
// ============================================================================

/**
 * Complete name validation (required + no whitespace + special chars)
 */
export const validateName = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return t('validation.required');
        if (String(value).includes(' ')) return t('validation.noWhitespaces');
        if (!/^[a-zA-Z0-9_\-=]+$/.test(String(value))) return t('validation.limitSpecialCharacters');
        return '';
    };
};

/**
 * Complete IP address validation (required + IP format)
 */
export const validateIpAddress = (t: TFunction): ValidationRule => {
    return ipAddress(t);
};

/**
 * Complete port validation (required + range check)
 */
export const validatePort = (t: TFunction): ValidationRule => {
    return port(t);
};

/**
 * Complete email validation (required + email format)
 */
export const validateEmail = (t: TFunction): ValidationRule => {
    return (value: any) => {
        if (!value) return t('validation.required');

        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return pattern.test(value) ? '' : t('validation.invalidEmail');
    };
};

// ============================================================================
// Parameterized Validation Rules
// ============================================================================

/**
 * Validates if a value is within the allowed range for Tx Power in dBm
 * @param txPowerInDbmNumbers - Allowed values for Tx Power
 */
export const validateTxPowerInDbm = (txPowerInDbmNumbers: number[]) => {
    return (t: TFunction): ValidationRule => {
        return (value: any) => {
            if (value === null || value === undefined) return t('validation.required');

            const num = Number(value);
            if (isNaN(num)) return t('validation.required');

            const minimum = Math.min(...txPowerInDbmNumbers);
            const maximum = Math.max(...txPowerInDbmNumbers);

            return txPowerInDbmNumbers.includes(num) ? '' : t('validation.valueBetween', { minimum, maximum });
        };
    };
};

/**
 * Validates if a value is within the allowed range for Rx Sensitivity in dBm
 * @param rxSensitivityInDbmNumbers - Allowed values for Rx Sensitivity
 */
export const validateRxSensitivityInDbm = (rxSensitivityInDbmNumbers: number[]) => {
    return (t: TFunction): ValidationRule => {
        return (value: any) => {
            if (value === null || value === undefined) return t('validation.required');

            const num = Number(value);
            if (isNaN(num)) return t('validation.required');

            const minimum = Math.min(...rxSensitivityInDbmNumbers);
            const maximum = Math.max(...rxSensitivityInDbmNumbers);

            return rxSensitivityInDbmNumbers.includes(num) ? '' : t('validation.valueBetween', { minimum, maximum });
        };
    };
};

/**
 * Validates if a date/time is valid
 * @param isValidDateTime - Function to validate date/time
 */
export const validateDateTime = (isValidDateTime: (value: any) => boolean) => {
    return (t: TFunction): ValidationRule => {
        return (value: any) => {
            return isValidDateTime(value) ? '' : t('validation.invalidDateTime');
        };
    };
};

// ============================================================================
// Rule Arrays (for convenience)
// ============================================================================

/**
 * Array of validation rules for names
 * Use this for complete name validation in one go
 */
export const nameValidationRules = (t: TFunction): ValidationRule[] => [
    required(t),
    noWhitespace(t),
    limitSpecialCharacters(t)
];

/**
 * Array of validation rules for IP addresses
 */
export const ipAddressRules = (t: TFunction): ValidationRule[] => [required(t), ipAddress(t)];

/**
 * Array of validation rules for ports
 */
export const portRules = (t: TFunction): ValidationRule[] => [required(t), port(t)];

/**
 * Array of validation rules for email addresses
 */
export const emailRules = (t: TFunction): ValidationRule[] => [required(t), validEmail(t)];

/**
 * Array of validation rules for passwords
 */
export const passwordRules = (t: TFunction): ValidationRule[] => [required(t), passwordValidation(t)];

/**
 * Array of validation rules for user names (first name, last name)
 */
export const userNameRules = (t: TFunction): ValidationRule[] => [required(t), validateUserName(t)];

/**
 * Array of validation rules for a Mender CID.
 * (Optional on create, but strictly validated if a user enters something).
 */
export const cidRules = (t: TFunction): ValidationRule[] => [validateCid(t)];

/**
 * Custom validation rule
 */
export const custom = (validator: (value: any) => boolean, message: string): ValidationRule => {
    return (value: any) => {
        return validator(value) ? '' : message;
    };
};
